import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../config/api";
import { useAuth } from "../contexts/AuthContext";
import { useSeason } from "../contexts/SeasonContext";
import { PlayerStats } from "../types";
import splLogo from "../img/SPL_Logo.png";
import spcLogo from "../img/SPC.png";
import "./LandingTwo.css";

/* ============================================================
   Types
   ============================================================ */

type Brand = "spl" | "spc" | "spt";

interface HeroStats {
  activePlayers: number;
  leagueDays: number;
  matchesPlayed: number;
}

interface DivComparisonItem {
  division: string;
  totalPlayers: number;
  totalLeagueDays: number;
  totalMatches: number;
  topWinRate?: number;
  avgPoints?: number;
}

interface LeagueDayBrief {
  id: string;
  name?: string;
  date?: string;
  registrationOpensAt?: string;
  division?: string;
  isCompleted?: boolean;
  isActive?: boolean;
  playerAssignments?: any[];
}

interface Tournament {
  id: string;
  name: string;
  status: "Coming Soon" | "Registration Open" | "In Progress" | "Completed";
  backendStatus?: string;
  level?: string;
  date?: string;
  venue?: string;
  teams?: number;
  maxTeams?: number;
  posterUrl?: string;
  description?: string;
  registeredTeams?: any[];
  sponsors?: { name: string; logoUrl?: string; url?: string }[];
}

interface BadgeItem {
  id: string;
  icon: string;
  name: string;
  description: string;
}

/* ============================================================
   Brand metadata
   ============================================================ */

const BRAND_META: Record<Brand, { mono: string; full: string; logo: string; eyebrow: string; h1: React.ReactNode; sub: string; defaultDark: boolean }> = {
  spl: {
    mono: "SPL", full: "Stuttgart Padel League", logo: splLogo,
    eyebrow: "Stuttgart Padel League",
    h1: <>Where Stuttgart padel <span className="lt-accentword">ranks</span> are decided.</>,
    sub: "The competitive community league. Track your ranking, register for league days, and climb the divisions.",
    defaultDark: false,
  },
  spc: {
    mono: "SPC", full: "Stuttgart Padel Cups", logo: spcLogo,
    eyebrow: "Stuttgart Padel Cups",
    h1: <>Nations rise. <span className="lt-accentword">Champions</span> reign.</>,
    sub: "Elite cup competitions that define national prestige. Spectate, compete, and follow the drama.",
    defaultDark: true,
  },
  spt: {
    mono: "SPT", full: "Stuttgart Padel Tournaments", logo: splLogo,
    eyebrow: "Stuttgart Padel Tournaments",
    h1: <>Enter. Battle. <span className="lt-accentword">Win.</span> Repeat.</>,
    sub: "Open tournaments for every level. Register your team — spots fill fast.",
    defaultDark: true,
  },
};

/* ============================================================
   Small helpers
   ============================================================ */

function initials(name: string): string {
  return name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);
}

function avatarHue(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return Math.abs(h) % 360;
}

function LtAvatar({ name, size = 42 }: { name: string; size?: number }) {
  const hue = avatarHue(name);
  const bg = `oklch(0.65 0.13 ${hue})`;
  return (
    <div
      className="lt-avatar"
      style={{ width: size, height: size, background: bg, borderRadius: size > 50 ? 18 : 12 }}
    >
      {initials(name)}
    </div>
  );
}

function pad2(n: number) {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}

/* ============================================================
   AccentBar
   ============================================================ */

function AccentBar() {
  return <div className="lt-accent-bar" />;
}

/* ============================================================
   Nav
   ============================================================ */

interface NavProps {
  brand: Brand;
  dark: boolean;
  onToggleMode: () => void;
  loggedIn: boolean;
  isAdmin: boolean;
  onLogin: () => void;
  onDash: () => void;
  onOpenDrawer: () => void;
}

function Nav({ brand, dark, onToggleMode, loggedIn, isAdmin, onLogin, onDash, onOpenDrawer }: NavProps) {
  const meta = BRAND_META[brand];
  return (
    <nav className="lt-nav">
      <div className="lt-wrap">
        <div className="lt-nav-inner">
          {loggedIn && isAdmin && (
            <button className="lt-icon-btn" onClick={onOpenDrawer} title="Admin menu">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}
          <div className="lt-nav-brand">
            <div className="lt-nav-logo">
              <img src={meta.logo} alt={meta.mono} />
            </div>
            <div className="lt-nav-name">
              <b>{meta.mono}</b>
              <span>{meta.full}</span>
            </div>
          </div>
          <div className="lt-nav-spacer" />
          <div className="lt-nav-actions">
            <button className="lt-icon-btn" onClick={onToggleMode} title={dark ? "Switch to light" : "Switch to dark"}>
              {dark ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              )}
            </button>
            {loggedIn ? (
              <button className="lt-btn" onClick={onDash}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 16, height: 16 }}>
                  <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
                </svg>
                Dashboard
              </button>
            ) : (
              <button className="lt-btn lt-btn-primary" onClick={onLogin}>Log in</button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

/* ============================================================
   Marquee
   ============================================================ */

function Marquee({ items }: { items: string[] }) {
  if (!items.length) return null;
  const doubled = [...items, ...items];
  return (
    <div className="lt-marquee">
      <div className="lt-marquee-track">
        {doubled.map((item, i) => (
          <span className="lt-marquee-item" key={i}>{item}</span>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Reg Banner
   ============================================================ */

function RegBanner({ onRegister }: { onRegister: () => void }) {
  return (
    <div className="lt-regbar">
      <div className="lt-wrap">
        <div className="lt-regbar-inner">
          <div className="lt-pulse" />
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text-2)", flex: 1 }}>
            Season registration is open — secure your spot for the upcoming league season.
          </span>
          <button className="lt-btn lt-btn-primary lt-btn-sm" onClick={onRegister}>Register team</button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Hero tab toggle
   ============================================================ */

interface HeroTabsProps {
  tab: Brand;
  setTab: (b: Brand) => void;
}

function HeroTabs({ tab, setTab }: HeroTabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [pillStyle, setPillStyle] = useState({ left: 0, width: 0 });
  const tabs: Brand[] = ["spl", "spc", "spt"];
  const labels: Record<Brand, string> = { spl: "League", spc: "Cups", spt: "Tournaments" };

  useEffect(() => {
    const idx = tabs.indexOf(tab);
    const el = tabRefs.current[idx];
    if (el) {
      const parent = el.parentElement;
      if (parent) {
        const pr = parent.getBoundingClientRect();
        const r = el.getBoundingClientRect();
        setPillStyle({ left: r.left - pr.left, width: r.width });
      }
    }
  }, [tab]);

  return (
    <div className="lt-tabs">
      <div className="lt-tab-pill" style={pillStyle} />
      {tabs.map((t, i) => (
        <button
          key={t}
          ref={(el) => { tabRefs.current[i] = el; }}
          className={`lt-tab${tab === t ? " active" : ""}`}
          onClick={() => setTab(t)}
        >
          <b>{t.toUpperCase()}</b>
          <small>{labels[t]}</small>
        </button>
      ))}
    </div>
  );
}

/* ============================================================
   Hero stat / mood strips
   ============================================================ */

function StatStrip({ stats }: { stats: HeroStats }) {
  return (
    <div className="lt-strip">
      <div className="lt-stat-chip"><span className="v lt-num">{stats.activePlayers}</span><span className="k">Active Players</span></div>
      <div className="lt-stat-chip"><span className="v lt-num">{stats.leagueDays}</span><span className="k">League Days</span></div>
      <div className="lt-stat-chip"><span className="v lt-num">{stats.matchesPlayed}</span><span className="k">Matches Played</span></div>
    </div>
  );
}

const MOOD_SPC = [
  { t: "RISE", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 2c0 6-6 9-6 13a6 6 0 0 0 12 0c0-4-6-7-6-13z" /></svg> },
  { t: "DOMINATE", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg> },
  { t: "CONQUER", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14.5 17.5L3 6V3h3l11.5 11.5" /><path d="m13 19 9-9" /><path d="m2 22 7.5-7.5" /></svg> },
  { t: "REIGN", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m2 4 3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14" /></svg> },
];
const MOOD_SPT = [
  { t: "ENTER", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" /></svg> },
  { t: "BATTLE", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14.5 17.5L3 6V3h3l11.5 11.5" /><path d="m13 19 9-9" /><path d="m2 22 7.5-7.5" /></svg> },
  { t: "WIN", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2z" /></svg> },
  { t: "REPEAT", icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="17 1 21 5 17 9" /><path d="M3 11V9a4 4 0 0 1 4-4h14" /><polyline points="7 23 3 19 7 15" /><path d="M21 13v2a4 4 0 0 1-4 4H3" /></svg> },
];

function MoodStrip({ moods }: { moods: { t: string; icon: React.ReactNode }[] }) {
  return (
    <div className="lt-strip">
      {moods.map((m) => (
        <div className="lt-mood-chip" key={m.t}>{m.icon}{m.t}</div>
      ))}
    </div>
  );
}

/* ============================================================
   Hero
   ============================================================ */

interface HeroProps {
  brand: Brand;
  tab: Brand;
  setTab: (b: Brand) => void;
  heroStats: HeroStats | null;
}

function Hero({ brand, tab, setTab, heroStats }: HeroProps) {
  const meta = BRAND_META[brand];
  return (
    <div className="lt-hero">
      <div className="lt-hero-glow" />
      <div className="lt-wrap">
        <div className="lt-hero-inner">
          <div className="lt-hero-logo">
            <img src={meta.logo} alt={meta.mono} />
          </div>
          <div className="lt-hero-eyebrow">{meta.eyebrow}</div>
          <h1>{meta.h1}</h1>
          <p className="lt-hero-sub">{meta.sub}</p>
          <HeroTabs tab={tab} setTab={setTab} />
          {tab === "spl" && heroStats && <StatStrip stats={heroStats} />}
          {tab === "spc" && <MoodStrip moods={MOOD_SPC} />}
          {tab === "spt" && <MoodStrip moods={MOOD_SPT} />}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Countdown
   ============================================================ */

function useCountdown(target: Date | null) {
  const [diff, setDiff] = useState(0);
  useEffect(() => {
    if (!target) return;
    const tick = () => setDiff(Math.max(0, target.getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [target]);
  const s = Math.floor(diff / 1000);
  return {
    days: Math.floor(s / 86400),
    hrs: Math.floor((s % 86400) / 3600),
    min: Math.floor((s % 3600) / 60),
    sec: s % 60,
  };
}

function CountdownSection({ leagueDay, loggedIn, onAction }: { leagueDay: LeagueDayBrief | null; loggedIn: boolean; onAction: () => void }) {
  const target = leagueDay?.registrationOpensAt ? new Date(leagueDay.registrationOpensAt) : null;
  const { days, hrs, min, sec } = useCountdown(target);
  if (!leagueDay) return null;

  const dateStr = leagueDay.date
    ? new Date(leagueDay.date).toLocaleDateString("en-DE", { weekday: "long", month: "long", day: "numeric" })
    : "Date TBD";

  return (
    <div className="lt-countdown">
      <div className="lt-countdown-left">
        <div className="lt-kicker">Next League Day · {target ? "Registration opens in" : "Coming up"}</div>
        <h3>{leagueDay.name || "Upcoming League Day"}</h3>
        <p>{dateStr}{leagueDay.division ? ` · ${leagueDay.division}` : ""}</p>
        <button className="lt-btn lt-btn-primary" onClick={onAction}>
          {loggedIn ? "Set a reminder" : "Log in to register"}
        </button>
      </div>
      {target && (
        <div className="lt-cd-clocks">
          <div className="lt-cd-unit"><span className="v lt-num">{pad2(days)}</span><span className="k">Days</span></div>
          <div className="lt-cd-unit"><span className="v lt-num">{pad2(hrs)}</span><span className="k">Hrs</span></div>
          <div className="lt-cd-unit"><span className="v lt-num">{pad2(min)}</span><span className="k">Min</span></div>
          <div className="lt-cd-unit"><span className="v lt-num">{pad2(sec)}</span><span className="k">Sec</span></div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Rankings table
   ============================================================ */

interface H2HTarget {
  a: PlayerStats;
  b?: PlayerStats;
}

interface RankingsProps {
  rankings: PlayerStats[];
  division: string;
  allDivisions: string[];
  currentUserId?: string;
  onH2H: (t: H2HTarget) => void;
}

const SWORDS_ICON = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
    <path d="m13 19 9-9" /><path d="m2 22 7.5-7.5" />
  </svg>
);

function RankRow({
  stat, rank, isMe, onH2H, allRankings,
}: {
  stat: PlayerStats; rank: number; isMe: boolean; onH2H: (t: H2HTarget) => void; allRankings: PlayerStats[];
}) {
  const winRate = typeof stat.bayesianWinRate === "number"
    ? stat.bayesianWinRate
    : stat.gamesPlayed > 0
    ? ((stat.gamesWon || 0) + 12 * 0.5) / ((stat.gamesPlayed || 0) + 12)
    : 0.5;
  const pct = Math.round(winRate * 100);
  const delta = stat.rankChange ?? 0;

  return (
    <div className={`lt-rank-row${isMe ? " lt-me" : ""}`}>
      <div className="lt-rk">
        <span className={rank <= 3 ? "lt-rk-medal" : ""}>{rank}</span>
        {delta !== 0 && (
          <span className={`lt-delta ${delta > 0 ? "lt-delta-up" : "lt-delta-down"}`}>
            {delta > 0 ? `▲${delta}` : `▼${Math.abs(delta)}`}
          </span>
        )}
        {delta === 0 && <span className="lt-delta lt-delta-same">—</span>}
      </div>
      <div className="lt-player">
        <LtAvatar name={stat.playerName} />
        <div className="lt-player-meta">
          <div className="lt-player-name">
            {stat.playerName}{isMe && <span style={{ color: "var(--accent-strong)", marginLeft: 6, fontSize: 12 }}>· You</span>}
          </div>
          {stat.badges && stat.badges.length > 0 && (
            <div className="lt-player-badges">
              {stat.badges.slice(0, 4).map((b, i) => (
                <div className="lt-bdg" key={i} title={b.type}>🏅</div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="lt-div-badge lt-col-div">{stat.division || "—"}</div>
      <div className="lt-pts lt-num">{(stat.totalPoints ?? 0).toLocaleString()}</div>
      <div className="lt-games lt-num lt-col-games">{stat.gamesWon ?? 0}/{stat.gamesPlayed ?? 0}</div>
      <div className="lt-winrate lt-col-wr">
        <span className="wv lt-num">{pct}%</span>
        <div className="lt-wbar"><i style={{ width: `${pct}%` }} /></div>
      </div>
      <button
        className="lt-h2h-btn lt-col-h2h"
        onClick={(e) => { e.stopPropagation(); const b = allRankings.find((r) => r.playerId !== stat.playerId); onH2H({ a: stat, b }); }}
        title="Head to head"
      >
        {SWORDS_ICON}
      </button>
    </div>
  );
}

function RankingsTable({ rankings, division, currentUserId, onH2H }: RankingsProps) {
  const grouped = division === "all"
    ? Object.entries(
        rankings.reduce<Record<string, PlayerStats[]>>((acc, r) => {
          const d = r.division || "Unknown";
          (acc[d] = acc[d] || []).push(r);
          return acc;
        }, {})
      )
    : [["", rankings] as [string, PlayerStats[]]];

  return (
    <div className="lt-card lt-rank-wrap">
      <div className="lt-rank-head">
        <span>Rank</span><span>Player</span>
        <span className="lt-col-div">Division</span>
        <span>Points</span>
        <span className="lt-col-games">W/P</span>
        <span className="lt-col-wr">Win rate</span>
        <span className="lt-col-h2h" />
      </div>
      {grouped.map(([div, rows]) => {
        let rankCounter = 0;
        return (
          <React.Fragment key={div}>
            {division === "all" && div && <div className="lt-div-header">{div}</div>}
            {(rows as PlayerStats[]).map((stat, idx) => {
              rankCounter++;
              const isPromo = idx === 0 && rows.length > 2;
              const isReleg = idx === rows.length - 2 && rows.length > 2;
              const isMe = stat.playerId === currentUserId;
              return (
                <React.Fragment key={stat.playerId}>
                  {isPromo && (
                    <div className="lt-zone-label lt-zone-promo">▲ Promotion zone — top 2 move up</div>
                  )}
                  {isReleg && (
                    <div className="lt-zone-label lt-zone-releg">▼ Relegation zone — bottom 2 drop down</div>
                  )}
                  <RankRow
                    stat={stat} rank={division === "all" ? idx + 1 : rankCounter}
                    isMe={isMe} onH2H={onH2H} allRankings={rankings}
                  />
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ============================================================
   Division comparison
   ============================================================ */

function DivisionComparison({ data }: { data: DivComparisonItem[] }) {
  if (!data.length) return null;
  return (
    <div className="lt-divcompare">
      {data.map((d) => (
        <div className="lt-divcol" key={d.division}>
          <h4>{d.division}</h4>
          <div className="lt-divstat"><span>Players</span><b className="lt-num">{d.totalPlayers}</b></div>
          <div className="lt-divstat"><span>Avg points</span><b className="lt-num">{d.avgPoints != null ? Math.round(d.avgPoints) : "—"}</b></div>
          <div className="lt-divstat"><span>Matches played</span><b className="lt-num">{d.totalMatches}</b></div>
          <div className="lt-divstat"><span>Top win rate</span><b className="lt-num">{d.topWinRate != null ? `${Math.round(d.topWinRate * 100)}%` : "—"}</b></div>
          <div className="lt-divstat"><span>League days</span><b className="lt-num">{d.totalLeagueDays}</b></div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   Badge showcase
   ============================================================ */

function BadgeShowcaseLt({ badges }: { badges: BadgeItem[] }) {
  if (!badges.length) return null;
  return (
    <div className="lt-badge-grid">
      {badges.map((b) => (
        <div className="lt-badge-card" key={b.id}>
          <div className="lt-badge-ico">{b.icon}</div>
          <div>
            <b>{b.name}</b>
            <p>{b.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ============================================================
   SPC — Cup cards
   ============================================================ */

function SPCContent() {
  return (
    <div className="lt-cup-grid">
      {/* Nations League */}
      <div
        className="lt-cup-card"
        role="link"
        tabIndex={0}
        onClick={() => window.location.href = "/cupOfNations"}
        onKeyDown={(e) => e.key === "Enter" && (window.location.href = "/cupOfNations")}
      >
        <div className="lt-cup-status">
          <span className="lt-chip-dot" style={{ background: "var(--live)" }} />
          In Progress
        </div>
        <div className="lt-cup-trophy">🏆</div>
        <div>
          <h3>Nations League</h3>
          <p className="lt-cup-desc">
            Country-vs-country padel at its finest. National teams battle through a round-robin group stage and knockout finals for continental supremacy.
          </p>
          <div className="lt-flag-row">
            <span className="lt-flag">🇩🇪</span>
            <span className="lt-flag">🇪🇸</span>
            <span className="lt-flag">🇦🇷</span>
            <span className="lt-flag">🇧🇷</span>
            <span className="lt-flag-more">+ more nations</span>
          </div>
        </div>
        <div className="lt-cup-cta">
          View standings
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 16, height: 16 }}>
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </div>
      </div>

      {/* SPCup of Nations */}
      <div
        className="lt-cup-card"
        role="link"
        tabIndex={0}
        onClick={() => window.location.href = "/cupOfNations"}
        onKeyDown={(e) => e.key === "Enter" && (window.location.href = "/cupOfNations")}
      >
        <div className="lt-cup-status">
          <span className="lt-chip-dot" style={{ background: "var(--good)" }} />
          Season Active
        </div>
        <div className="lt-cup-trophy">🥇</div>
        <div>
          <h3>SPCup of Nations</h3>
          <p className="lt-cup-desc">
            The prestige cup. The definitive club-vs-nation championship — where Stuttgart's best meet the world's finest in a single-elimination showdown.
          </p>
          <div className="lt-flag-row">
            <span className="lt-flag">🇩🇪</span>
            <span className="lt-flag">🇮🇹</span>
            <span className="lt-flag">🇵🇹</span>
            <span className="lt-flag">🇲🇽</span>
            <span className="lt-flag-more">+ all nations</span>
          </div>
        </div>
        <div className="lt-cup-cta">
          See results
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ width: 16, height: 16 }}>
            <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
          </svg>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   SPT — Tournament grid
   ============================================================ */

const STATUS_META: Record<string, { color: string; cta: string }> = {
  "Coming Soon": { color: "var(--warn)", cta: "Get notified" },
  "Registration Open": { color: "var(--good)", cta: "Register now" },
  "In Progress": { color: "var(--live)", cta: "View bracket" },
  "Completed": { color: "var(--accent)", cta: "See results" },
};

function TournamentCard({
  t,
  navigate,
  loggedIn,
  currentUserId,
  registrations,
  pendingInvitations,
  receivedInvitations,
  processingInviteId,
  onAcceptInvitation,
  onDeclineInvitation,
  onCancelInvitation,
  onWithdrawRegistration,
}: {
  t: Tournament;
  navigate: (p: string) => void;
  loggedIn: boolean;
  currentUserId?: string;
  registrations: any[];
  pendingInvitations: any[];
  receivedInvitations: any[];
  processingInviteId: string | null;
  onAcceptInvitation: (invitationId: string) => void;
  onDeclineInvitation: (invitationId: string) => void;
  onCancelInvitation: (invitationId: string) => void;
  onWithdrawRegistration: (registrationId: string, tournamentName: string) => void;
}) {
  const meta = STATUS_META[t.status] || { color: "var(--text-3)", cta: "View" };
  const teamsCount = Array.isArray(t.registeredTeams) ? t.registeredTeams.length : (t.teams ?? 0);
  const pct = t.maxTeams && teamsCount != null ? Math.round((teamsCount / t.maxTeams) * 100) : 0;
  const dateStr = t.date
    ? new Date(t.date).toLocaleDateString("en-DE", { month: "short", day: "numeric", year: "numeric" })
    : "TBD";

  const isRegistrationOpen = t.backendStatus === "registration" || t.status === "Registration Open";

  let myRegistration: any = registrations.find((reg: any) => reg.tournamentId === t.id);
  const registeredTeam = t.registeredTeams?.find(
    (team: any) => team.player1Id === currentUserId || team.player2Id === currentUserId,
  );
  const myPendingInvitation = pendingInvitations.find((inv: any) => inv.tournamentId === t.id);
  const myReceivedInvitation = receivedInvitations.find((inv: any) => inv.tournamentId === t.id);

  let registrationStatus = "none";
  let partnerName = "";
  let invitationId = "";

  if (!myRegistration && registeredTeam) {
    myRegistration = {
      id: registeredTeam.id,
      tournamentId: t.id,
      status: "confirmed",
      player1Id: registeredTeam.player1Id,
      player2Id: registeredTeam.player2Id,
      player1Name: registeredTeam.player1Name,
      player2Name: registeredTeam.player2Name,
    };
  }

  if (myRegistration) {
    if (myRegistration.status === "confirmed") {
      registrationStatus = "registered";
      partnerName = currentUserId === myRegistration.player1Id ? myRegistration.player2Name : myRegistration.player1Name;
    } else if (myRegistration.status === "pending-approval") {
      registrationStatus = "pending-approval";
      partnerName = currentUserId === myRegistration.player1Id ? myRegistration.player2Name : myRegistration.player1Name;
    } else if (myRegistration.status === "waitlist") {
      registrationStatus = "waitlisted";
      partnerName = currentUserId === myRegistration.player1Id ? myRegistration.player2Name : myRegistration.player1Name;
    }
  } else if (myPendingInvitation) {
    if (myPendingInvitation.isNewPlayer) {
      registrationStatus = "awaiting-registration";
      partnerName = myPendingInvitation.recipientName || myPendingInvitation.recipientEmail;
      invitationId = myPendingInvitation.id;
    } else {
      registrationStatus = "awaiting-response";
      partnerName = myPendingInvitation.recipientName || myPendingInvitation.recipientEmail;
      invitationId = myPendingInvitation.id;
    }
  }

  const checklistEntry =
    currentUserId && (t as any).playerChecklist
      ? (t as any).playerChecklist[currentUserId]
      : null;
  const teammatePlayerId =
    myRegistration && currentUserId
      ? (currentUserId === myRegistration.player1Id
          ? myRegistration.player2Id
          : myRegistration.player1Id)
      : null;
  const teammateChecklistEntry =
    teammatePlayerId && (t as any).playerChecklist
      ? (t as any).playerChecklist[teammatePlayerId]
      : null;
  const withdrawalBlockedByPayment = !!(checklistEntry?.paid || teammateChecklistEntry?.paid);
  const withdrawalBlockedMessage = "To cancel your registration please contact the tournament organizers directly on WhatsApp at +49 176 2019 3968.";

  return (
    <div className="lt-tourny" role="link" tabIndex={0}
      onClick={() => navigate(`/tournament/${t.id}`)}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/tournament/${t.id}`)}
    >
      <div className="lt-tourny-poster">
        {t.posterUrl
          ? <img src={t.posterUrl} alt={t.name} />
          : <div className="lt-tourny-poster-ph">🎾</div>}
        <div className="lt-tourny-chips">
          <span className="lt-chip lt-chip-status">
            <span className="lt-chip-dot" style={{ background: meta.color }} />
            {t.status}
          </span>
          {t.level && <span className="lt-chip lt-chip-level">{t.level}</span>}
        </div>
      </div>
      <div className="lt-tourny-body">
        <h3>{t.name}</h3>
        <div className="lt-tourny-meta">
          <div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {dateStr}
          </div>
          {t.venue && (
            <div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
              {t.venue}
            </div>
          )}
                {t.maxTeams != null && (
            <>
              <div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                      {teamsCount} / {t.maxTeams} teams
              </div>
              <div className="lt-teams-bar"><i style={{ width: `${pct}%` }} /></div>
            </>
          )}
        </div>
      </div>
      <div className="lt-tourny-foot">
        <div className="lt-sponsors">
          {t.sponsors?.length ? (
            <>
              <small>Sponsored by</small>
              {t.sponsors.map((s, i) =>
                s.url ? (
                  <a
                    key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                    className="lt-sponsor-logo" title={s.name}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {s.name}
                  </a>
                ) : (
                  <span key={i} className="lt-sponsor-logo" title={s.name}>{s.name}</span>
                )
              )}
            </>
          ) : null}
        </div>
              <div className="lt-tourny-cta" onClick={(e) => e.stopPropagation()}>
                {!loggedIn && (
                  <button className="lt-btn lt-btn-sm lt-btn-primary" onClick={() => navigate("/player/login")}>Log in to register</button>
                )}

                {loggedIn && myReceivedInvitation && isRegistrationOpen && !myRegistration && (
                  <div className="lt-tourny-action-col">
                    <div className="lt-tourny-note">Invited by {myReceivedInvitation.senderPlayerName}</div>
                    <div className="lt-tourny-action-row">
                      <button className="lt-btn lt-btn-sm" onClick={() => onDeclineInvitation(myReceivedInvitation.id)} disabled={processingInviteId !== null}>
                        {processingInviteId === myReceivedInvitation.id ? "..." : "Decline"}
                      </button>
                      <button className="lt-btn lt-btn-sm lt-btn-primary" onClick={() => onAcceptInvitation(myReceivedInvitation.id)} disabled={processingInviteId !== null}>
                        {processingInviteId === myReceivedInvitation.id ? "..." : "Accept"}
                      </button>
                    </div>
                  </div>
                )}

                {loggedIn && (!myReceivedInvitation || !isRegistrationOpen || !!myRegistration) && registrationStatus === "none" && (
                  <button className="lt-btn lt-btn-sm lt-btn-primary" onClick={() => navigate("/view-tournaments")}>{meta.cta}</button>
                )}

                {loggedIn && registrationStatus === "awaiting-response" && (
                  <div className="lt-tourny-action-col">
                    <div className="lt-tourny-note">Awaiting {partnerName}'s response</div>
                    <button className="lt-btn lt-btn-sm" onClick={() => onCancelInvitation(invitationId)} disabled={processingInviteId !== null}>Cancel invitation</button>
                  </div>
                )}

                {loggedIn && registrationStatus === "awaiting-registration" && (
                  <div className="lt-tourny-action-col">
                    <div className="lt-tourny-note">Awaiting {partnerName}'s registration</div>
                    <button className="lt-btn lt-btn-sm" onClick={() => onCancelInvitation(invitationId)} disabled={processingInviteId !== null}>Cancel invitation</button>
                  </div>
                )}

                {loggedIn && registrationStatus === "pending-approval" && (
                  <div className="lt-tourny-action-col">
                    <div className="lt-tourny-note">Pending admin approval{partnerName ? ` with ${partnerName}` : ""}</div>
                  </div>
                )}

                {loggedIn && (registrationStatus === "registered" || registrationStatus === "waitlisted") && (
                  <div className="lt-tourny-action-col">
                    <div className="lt-tourny-note">{registrationStatus === "waitlisted" ? "Waitlisted" : "Registered"}{partnerName ? ` with ${partnerName}` : ""}</div>
                    {myRegistration?.id && (
                      <button className="lt-btn lt-btn-sm" disabled={withdrawalBlockedByPayment} onClick={() => {
                        if (withdrawalBlockedByPayment) return;
                        onWithdrawRegistration(myRegistration.id, t.name);
                      }}>{withdrawalBlockedByPayment ? withdrawalBlockedMessage : "Withdraw"}</button>
                    )}
                  </div>
                )}
              </div>
      </div>
    </div>
  );
}

function SPTContent({
  tournaments,
  navigate,
  loggedIn,
  currentUserId,
  registrations,
  pendingInvitations,
  receivedInvitations,
  processingInviteId,
  onAcceptInvitation,
  onDeclineInvitation,
  onCancelInvitation,
  onWithdrawRegistration,
}: {
  tournaments: Tournament[];
  navigate: (p: string) => void;
  loggedIn: boolean;
  currentUserId?: string;
  registrations: any[];
  pendingInvitations: any[];
  receivedInvitations: any[];
  processingInviteId: string | null;
  onAcceptInvitation: (invitationId: string) => void;
  onDeclineInvitation: (invitationId: string) => void;
  onCancelInvitation: (invitationId: string) => void;
  onWithdrawRegistration: (registrationId: string, tournamentName: string) => void;
}) {
  if (!tournaments.length) {
    return (
      <div className="lt-empty-state">
        <div className="e-ico">🎾</div>
        <h3>No tournaments yet</h3>
        <p style={{ marginTop: 6, fontSize: 14 }}>Check back soon — new tournaments are posted regularly.</p>
      </div>
    );
  }
  return (
    <div className="lt-tourny-grid">
      {tournaments.map((t) => (
        <TournamentCard
          key={t.id}
          t={t}
          navigate={navigate}
          loggedIn={loggedIn}
          currentUserId={currentUserId}
          registrations={registrations}
          pendingInvitations={pendingInvitations}
          receivedInvitations={receivedInvitations}
          processingInviteId={processingInviteId}
          onAcceptInvitation={onAcceptInvitation}
          onDeclineInvitation={onDeclineInvitation}
          onCancelInvitation={onCancelInvitation}
          onWithdrawRegistration={onWithdrawRegistration}
        />
      ))}
    </div>
  );
}

/* ============================================================
   H2H Dialog
   ============================================================ */

function H2HDialog({ initial, allPlayers, onClose }: { initial: H2HTarget; allPlayers: PlayerStats[]; onClose: () => void }) {
  const [a, setA] = useState<PlayerStats>(initial.a);
  const [b, setB] = useState<PlayerStats | undefined>(initial.b ?? allPlayers.find((p) => p.playerId !== initial.a.playerId));
  const [picking, setPicking] = useState<"a" | "b" | null>(null);
  const [search, setSearch] = useState("");

  const winRateOf = (s: PlayerStats) => {
    if (typeof s.bayesianWinRate === "number") return s.bayesianWinRate;
    return s.gamesPlayed > 0 ? ((s.gamesWon || 0) + 12 * 0.5) / ((s.gamesPlayed || 0) + 12) : 0.5;
  };

  const aRate = Math.round(winRateOf(a) * 100);
  const bRate = b ? Math.round(winRateOf(b) * 100) : 0;
  const total = aRate + bRate || 1;

  const filteredPlayers = allPlayers.filter((p) => p.playerName.toLowerCase().includes(search.toLowerCase()));

  const choose = (p: PlayerStats) => {
    if (picking === "a") setA(p);
    else if (picking === "b") setB(p);
    setPicking(null);
    setSearch("");
  };

  const rows: [string, number | string, number | string, boolean][] = b ? [
    ["Points", a.totalPoints ?? 0, b.totalPoints ?? 0, true],
    ["Win rate (Bayesian)", `${aRate}%`, `${bRate}%`, true],
    ["Games won", a.gamesWon ?? 0, b.gamesWon ?? 0, true],
    ["Games played", a.gamesPlayed ?? 0, b.gamesPlayed ?? 0, false],
    ["Division", a.division || "—", b.division || "—", false],
  ] : [];

  return (
    <div className="lt-dialog-scrim" onClick={onClose}>
      <div className="lt-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="lt-dialog-head">
          <h3>
            {SWORDS_ICON && <span style={{ display: "inline-flex", verticalAlign: "middle", marginRight: 8 }}>{SWORDS_ICON}</span>}
            Head-to-head
          </h3>
          <button className="lt-icon-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="lt-dialog-body">
          {picking ? (
            <div>
              <p style={{ marginTop: 0, color: "var(--text-2)", fontWeight: 600 }}>
                Pick a player for slot {picking.toUpperCase()}
              </p>
              <input
                className="lt-pick-search"
                placeholder="Search players…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
              <div className="lt-player-pick" style={{ marginTop: 10 }}>
                {filteredPlayers.map((p) => (
                  <div
                    key={p.playerId}
                    className={`lt-pick-row${(picking === "a" ? a.playerId : b?.playerId) === p.playerId ? " lt-sel" : ""}`}
                    onClick={() => choose(p)}
                  >
                    <LtAvatar name={p.playerName} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{p.playerName}</div>
                      <div style={{ fontSize: 12, color: "var(--text-3)" }}>
                        {p.division} · {(p.totalPoints ?? 0).toLocaleString()} pts
                      </div>
                    </div>
                    <span className="lt-div-badge">{p.division || "—"}</span>
                  </div>
                ))}
              </div>
              <button className="lt-btn lt-btn-sm" style={{ marginTop: 12 }} onClick={() => { setPicking(null); setSearch(""); }}>
                Cancel
              </button>
            </div>
          ) : (
            <>
              <div className="lt-h2h-vs">
                <div className="lt-h2h-player">
                  <LtAvatar name={a.playerName} size={64} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{a.playerName}</div>
                    <button className="lt-change-btn" onClick={() => setPicking("a")}>Change</button>
                  </div>
                </div>
                <div className="lt-h2h-vs-label">VS</div>
                {b ? (
                  <div className="lt-h2h-player">
                    <LtAvatar name={b.playerName} size={64} />
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 15 }}>{b.playerName}</div>
                      <button className="lt-change-btn" onClick={() => setPicking("b")}>Change</button>
                    </div>
                  </div>
                ) : (
                  <div className="lt-h2h-player">
                    <button className="lt-btn lt-btn-sm" onClick={() => setPicking("b")}>Pick player B</button>
                  </div>
                )}
              </div>
              {b && rows.map(([label, av, bv, higher], i) => {
                const aNum = typeof av === "number" ? av : parseFloat(String(av));
                const bNum = typeof bv === "number" ? bv : parseFloat(String(bv));
                const aWin = higher && aNum > bNum;
                const bWin = higher && bNum > aNum;
                return (
                  <div className="lt-h2h-stat" key={i}>
                    <span className={`val l lt-num${aWin ? " lt-win" : ""}`}>
                      {typeof av === "number" ? av.toLocaleString() : av}
                    </span>
                    <span className="lbl">{label}</span>
                    <span className={`val r lt-num${bWin ? " lt-win" : ""}`}>
                      {typeof bv === "number" ? bv.toLocaleString() : bv}
                    </span>
                  </div>
                );
              })}
              {b && (
                <>
                  <div className="lt-h2h-bar" style={{ marginTop: 16 }}>
                    <div style={{ width: `${(aRate / total) * 100}%`, background: "var(--accent)" }} />
                    <div style={{ width: `${(bRate / total) * 100}%`, background: "var(--support)" }} />
                  </div>
                  <p style={{ textAlign: "center", fontSize: 12, color: "var(--text-3)", marginBottom: 0, marginTop: 6 }}>
                    Bayesian win-rate share
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Admin drawer
   ============================================================ */

const DRAWER_LINKS = [
  { section: "League", links: [
    { label: "Divisions & Seasons", path: "/admin/seasons" },
    { label: "Players & Registrations", path: "/admin/players" },
    { label: "League Days", path: "/admin/league-days" },
  ]},
  { section: "Competitions", links: [
    { label: "Tournaments", path: "/admin/tournaments" },
    { label: "Cups", path: "/admin/cups" },
  ]},
  { section: "Settings", links: [
    { label: "Announcements", path: "/admin/announcements" },
    { label: "Sponsors", path: "/admin/sponsors" },
  ]},
];

function AdminDrawer({ onClose, navigate }: { onClose: () => void; navigate: (p: string) => void }) {
  return (
    <>
      <div className="lt-drawer-scrim" onClick={onClose} />
      <div className="lt-drawer">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "var(--text)" }}>Admin</span>
          <button className="lt-icon-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        {DRAWER_LINKS.map(({ section, links }) => (
          <React.Fragment key={section}>
            <h4>{section}</h4>
            {links.map(({ label, path }) => (
              <button
                key={path}
                className="lt-drawer-link"
                style={{ background: "none", border: "none", width: "100%", textAlign: "left" }}
                onClick={() => { navigate(path); onClose(); }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
                </svg>
                {label}
              </button>
            ))}
          </React.Fragment>
        ))}
      </div>
    </>
  );
}

/* ============================================================
   Footer
   ============================================================ */

function Footer({ brand }: { brand: Brand }) {
  const meta = BRAND_META[brand];
  return (
    <footer className="lt-footer">
      <div className="lt-wrap">
        <div className="lt-footer-inner">
          <div className="lt-footer-brand">
            <div className="lt-footer-logo"><img src={meta.logo} alt={meta.mono} /></div>
            <small><b>{meta.full}</b></small>
          </div>
          <div className="lt-footer-links">
            <a href="/home">Home</a>
            <a href="/view-tournaments">Tournaments</a>
            <a href="/cupOfNations">Nations League</a>
            <a href="/player/login">Player login</a>
          </div>
          <small>© {new Date().getFullYear()} Stuttgart Padel</small>
        </div>
      </div>
    </footer>
  );
}

/* ============================================================
   SPL page
   ============================================================ */

interface SPLPageProps {
  rankings: PlayerStats[];
  divisions: string[];
  divComparison: DivComparisonItem[];
  badges: BadgeItem[];
  nextLeagueDay: LeagueDayBrief | null;
  isLiveNow: boolean;
  loggedIn: boolean;
  currentUserId?: string;
  onH2H: (t: H2HTarget) => void;
  onRegister: () => void;
  selectedSeason: string;
  allSeasons: { id: string; name?: string; seasonName?: string }[];
  onSeasonChange: (id: string) => void;
}

function SPLPage({
  rankings, divisions, divComparison, badges, nextLeagueDay,
  isLiveNow, loggedIn, currentUserId, onH2H, onRegister,
  selectedSeason, allSeasons, onSeasonChange,
}: SPLPageProps) {
  const [div, setDiv] = useState("all");
  const [hideGuests, setHideGuests] = useState(true);

  const filtered = (div === "all" ? rankings : rankings.filter((r) => r.division === div))
    .filter((r) => !hideGuests || (!r.isGuest && !r.isGuestPlayer));

  return (
    <>
      {/* Season rankings header */}
      <section className="lt-section" style={{ paddingTop: 30 }}>
        <div className="lt-wrap">
          <div className="lt-section-head" style={{ marginBottom: 18 }}>
            <div>
              <div className="lt-kicker">League standings</div>
              <h2>Season rankings</h2>
            </div>
            <div className="lt-field">
              <select
                className="lt-select"
                value={selectedSeason}
                onChange={(e) => onSeasonChange(e.target.value)}
              >
                {allSeasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.seasonName || s.name || s.id}</option>
                ))}
              </select>
              <svg className="lt-field-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          </div>

          {isLiveNow && (
            <div className="lt-livenow" style={{ marginBottom: 18 }}>
              <div className="lt-live-dot" />
              <span className="lt-live-tag">LIVE NOW</span>
              <span style={{ flex: 1, fontSize: 14, color: "var(--text-2)" }}>
                A league day is in progress right now.
              </span>
              <button className="lt-btn lt-btn-sm" onClick={() => window.location.href = "/home"}>
                Follow live
              </button>
            </div>
          )}

          <CountdownSection leagueDay={nextLeagueDay} loggedIn={loggedIn} onAction={onRegister} />
        </div>
      </section>

      {/* Player rankings */}
      <section className="lt-section" style={{ paddingTop: 8 }}>
        <div className="lt-wrap">
          <div className="lt-section-head">
            <div>
              <div className="lt-kicker">Player rankings</div>
              <h2>{div === "all" ? "All divisions" : div}</h2>
              <p>Ranked by total points. Win rate is Bayesian-adjusted for games played.</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <div className="lt-seg">
                <button className={div === "all" ? "on" : ""} onClick={() => setDiv("all")}>All</button>
                {divisions.map((d) => (
                  <button key={d} className={div === d ? "on" : ""} onClick={() => setDiv(d)}>{d}</button>
                ))}
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: "var(--text-2)", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={hideGuests}
                  onChange={(e) => setHideGuests(e.target.checked)}
                  style={{ accentColor: "var(--accent)", width: 15, height: 15, cursor: "pointer" }}
                />
                Hide guests
              </label>
            </div>
          </div>
          {rankings.length > 0 ? (
            <RankingsTable rankings={filtered} division={div} allDivisions={divisions} currentUserId={currentUserId} onH2H={onH2H} />
          ) : (
            <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-3)" }}>
              No rankings data available for this season.
            </div>
          )}
        </div>
      </section>

      {/* Division comparison — only on All view */}
      {div === "all" && divComparison.length > 0 && (
        <section className="lt-section" style={{ paddingTop: 8 }}>
          <div className="lt-wrap">
            <div className="lt-section-head">
              <div>
                <div className="lt-kicker">Side by side</div>
                <h2>Division comparison</h2>
              </div>
            </div>
            <DivisionComparison data={divComparison} />
          </div>
        </section>
      )}

      {/* Badge showcase */}
      {badges.length > 0 && (
        <section className="lt-section" style={{ paddingTop: 8 }}>
          <div className="lt-wrap">
            <div className="lt-section-head">
              <div>
                <div className="lt-kicker">Achievements</div>
                <h2>Earnable badges</h2>
                <p>Unlock these across the season — they show on your ranking row.</p>
              </div>
            </div>
            <BadgeShowcaseLt badges={badges} />
          </div>
        </section>
      )}
    </>
  );
}

/* ============================================================
   Main LandingTwo component
   ============================================================ */

export default function LandingTwo() {
  const navigate = useNavigate();
  const { user, playerUser, playerToken, isAdmin } = useAuth();
  const { allSeasons, selectedSeason, setSelectedSeason } = useSeason();

  const loggedIn = !!(user || playerUser);
  const currentUserId = playerUser?.playerId || user?.id;

  /* Brand / mode state */
  const [tab, setTab] = useState<Brand>("spl");
  const [darkOverride, setDarkOverride] = useState<boolean | null>(null);
  const brandDark = BRAND_META[tab].defaultDark;
  const dark = darkOverride != null ? darkOverride : brandDark;

  const changeTab = useCallback((b: Brand) => {
    setDarkOverride(null);
    setTab(b);
  }, []);

  /* UI state */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [h2hTarget, setH2hTarget] = useState<H2HTarget | null>(null);

  /* Data state */
  const [announcements, setAnnouncements] = useState<string[]>([]);
  const [heroStats, setHeroStats] = useState<HeroStats | null>(null);
  const [rankings, setRankings] = useState<PlayerStats[]>([]);
  const [divisions, setDivisions] = useState<string[]>([]);
  const [divComparison, setDivComparison] = useState<DivComparisonItem[]>([]);
  const [nextLeagueDay, setNextLeagueDay] = useState<LeagueDayBrief | null>(null);
  const [isLiveNow, setIsLiveNow] = useState(false);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [receivedInvitations, setReceivedInvitations] = useState<any[]>([]);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [badges, setBadges] = useState<BadgeItem[]>([]);

  const seasonId = selectedSeason?.id;

  /* Fetch announcements */
  useEffect(() => {
    apiRequest("/api/announcements/active")
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (Array.isArray(data)) setAnnouncements(data.map((a: any) => a.message || a.text || String(a)));
        else if (data?.announcements) setAnnouncements(data.announcements.map((a: any) => a.message || a.text || String(a)));
      })
      .catch(() => {});
  }, []);

  /* Fetch division comparison + hero stats */
  useEffect(() => {
    const params = new URLSearchParams();
    if (seasonId) params.set("seasonId", seasonId);
    apiRequest(`/api/public/divisions/comparison?${params}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data: any[]) => {
        if (!Array.isArray(data)) return;
        const comp: DivComparisonItem[] = data.map((d) => ({
          division: d.division,
          totalPlayers: d.totalPlayers ?? 0,
          totalLeagueDays: d.totalLeagueDays ?? 0,
          totalMatches: d.totalMatches ?? 0,
          avgPoints: d.avgPoints,
          topWinRate: d.topWinRate,
        }));
        setDivComparison(comp);
        const totalPlayers = comp.reduce((s, d) => s + d.totalPlayers, 0);
        const totalLeagueDays = comp.reduce((s, d) => s + d.totalLeagueDays, 0);
        const totalMatches = comp.reduce((s, d) => s + d.totalMatches, 0);
        setHeroStats({ activePlayers: totalPlayers, leagueDays: totalLeagueDays, matchesPlayed: totalMatches });
        setDivisions(comp.map((d) => d.division).filter(Boolean));
      })
      .catch(() => {});
  }, [seasonId]);

  /* Fetch rankings — loop per division once we have the division list */
  useEffect(() => {
    if (!divisions.length) return;
    const fetchAll = async () => {
      const all: PlayerStats[] = [];
      for (const div of divisions) {
        const params = new URLSearchParams({ division: div, rankBy: "hybrid", participationWeight: "40" });
        if (seasonId) params.set("seasonId", seasonId);
        try {
          const r = await apiRequest(`/api/public/rankings?${params}`);
          if (r.ok) {
            const data = await r.json();
            const list: PlayerStats[] = Array.isArray(data) ? data : data?.rankings ?? [];
            all.push(...list);
          }
        } catch {}
      }
      setRankings(all);
    };
    fetchAll();
  }, [divisions, seasonId]);

  /* Fetch next league day — loop per division once available */
  useEffect(() => {
    if (!divisions.length) return;
    const fetchLeagueDays = async () => {
      const allDays: LeagueDayBrief[] = [];
      for (const div of divisions) {
        try {
          const r = await apiRequest(`/api/public/league-days?division=${div}`);
          if (r.ok) {
            const data = await r.json();
            const list: LeagueDayBrief[] = Array.isArray(data) ? data : data?.leagueDays ?? [];
            allDays.push(...list);
          }
        } catch {}
      }
      const now = Date.now();
      const upcoming = allDays
        .filter((d) => !d.isCompleted)
        .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime())[0] ?? null;
      setNextLeagueDay(upcoming);
      const live = allDays.some((d) => {
        if (!d.date || d.isCompleted) return false;
        return new Date(d.date).toDateString() === new Date(now).toDateString();
      });
      setIsLiveNow(live);
    };
    fetchLeagueDays();
  }, [divisions]);

  const mapTournamentStatus = (status: string): Tournament["status"] => {
    const raw = (status || "").toLowerCase();
    if (raw === "registration" || raw === "registration open") return "Registration Open";
    if (raw === "upcoming" || raw === "coming soon") return "Coming Soon";
    if (raw === "in progress" || raw === "in-progress" || raw === "group-stage" || raw === "knockout") return "In Progress";
    if (raw === "completed") return "Completed";
    return "Coming Soon";
  };

  const fetchTournaments = useCallback(() => {
    const params = new URLSearchParams({ playerView: "true" });
    if (playerUser?.playerId) params.set("playerId", playerUser.playerId);
    return apiRequest(`/api/tournaments?${params}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data: any) => {
        const list = Array.isArray(data) ? data : data?.tournaments ?? [];
        const mapped: Tournament[] = list.map((t: any) => ({
          id: t.id,
          name: t.name,
          status: mapTournamentStatus(t.status),
          backendStatus: t.status,
          level: t.level,
          date: t.date ?? t.startDate,
          venue: t.venue ?? t.location,
          teams: Array.isArray(t.registeredTeams) ? t.registeredTeams.length : (t.teams ?? 0),
          maxTeams: t.maxTeams ?? t.capacity,
          description: t.description,
          registeredTeams: Array.isArray(t.registeredTeams) ? t.registeredTeams : [],
          posterUrl: t.posterUrl ?? t.imageUrl,
          sponsors: t.sponsors ?? [],
        }));
        setTournaments(mapped);
      })
      .catch(() => {});
  }, [playerUser?.playerId]);

  const fetchTournamentPlayerState = useCallback(async () => {
    if (!playerUser || !playerToken) {
      setRegistrations([]);
      setPendingInvitations([]);
      setReceivedInvitations([]);
      return;
    }
    try {
      const [registrationsRes, sentRes, receivedRes] = await Promise.all([
        apiRequest("/api/tournament-registrations/my-registrations", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
        apiRequest("/api/tournament-invitations/sent", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
        apiRequest("/api/tournament-invitations/received", {
          headers: { Authorization: `Bearer ${playerToken}` },
        }),
      ]);

      if (registrationsRes.ok) {
        const regs = await registrationsRes.json();
        setRegistrations(Array.isArray(regs) ? regs : []);
      }
      if (sentRes.ok) {
        const sent = await sentRes.json();
        setPendingInvitations(Array.isArray(sent) ? sent.filter((inv: any) => inv.status === "pending") : []);
      }
      if (receivedRes.ok) {
        const received = await receivedRes.json();
        setReceivedInvitations(Array.isArray(received) ? received.filter((inv: any) => inv.status === "pending") : []);
      }
    } catch {
      // Keep landing page resilient if these endpoints fail.
    }
  }, [playerUser, playerToken]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  useEffect(() => {
    fetchTournamentPlayerState();
  }, [fetchTournamentPlayerState]);

  const handleAcceptInvitation = useCallback(async (invitationId: string) => {
    if (!playerToken) return;
    setProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await Promise.all([fetchTournamentPlayerState(), fetchTournaments()]);
    } catch {
      // Ignore here; /view-tournaments has full management UI.
    } finally {
      setProcessingInviteId(null);
    }
  }, [playerToken, fetchTournamentPlayerState, fetchTournaments]);

  const handleDeclineInvitation = useCallback(async (invitationId: string) => {
    if (!playerToken) return;
    setProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/decline`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await fetchTournamentPlayerState();
    } catch {
      // Ignore here; /view-tournaments has full management UI.
    } finally {
      setProcessingInviteId(null);
    }
  }, [playerToken, fetchTournamentPlayerState]);

  const handleCancelInvitation = useCallback(async (invitationId: string) => {
    if (!playerToken) return;
    setProcessingInviteId(invitationId);
    try {
      await apiRequest(`/api/tournament-invitations/${invitationId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      await fetchTournamentPlayerState();
    } catch {
      // Ignore here; /view-tournaments has full management UI.
    } finally {
      setProcessingInviteId(null);
    }
  }, [playerToken, fetchTournamentPlayerState]);

  const handleWithdrawRegistration = useCallback(async (registrationId: string, tournamentName: string) => {
    if (!playerToken) return;
    if (!window.confirm(`Are you sure you want to withdraw from ${tournamentName}?`)) return;
    try {
      const response = await apiRequest(`/api/tournament-registrations/${registrationId}/withdraw`, {
        method: "POST",
        headers: { Authorization: `Bearer ${playerToken}` },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || "Failed to withdraw registration");
      }
      await Promise.all([fetchTournamentPlayerState(), fetchTournaments()]);
    } catch (error: any) {
      alert(error?.message || "Failed to withdraw registration");
    }
  }, [playerToken, fetchTournamentPlayerState, fetchTournaments]);

  /* Fetch badge catalog */
  useEffect(() => {
    apiRequest("/api/badge-config")
      .then((r) => r.ok ? r.json() : null)
      .then((data: any) => {
        if (!data) return;
        const list = data.badges ?? data.seasonal ?? [];
        const milestone = data.milestone ?? [];
        const all = [...list, ...milestone];
        const mapped: BadgeItem[] = all.map((b: any) => ({
          id: b.id,
          icon: b.emoji ?? b.icon ?? "🏅",
          name: b.name ?? b.id,
          description: b.description ?? "",
        }));
        setBadges(mapped);
      })
      .catch(() => {});
  }, []);

  const handleLogin = useCallback(() => navigate("/player/login"), [navigate]);
  const handleDash = useCallback(() => navigate("/player/dashboard"), [navigate]);
  const handleRegister = useCallback(() => navigate(loggedIn ? "/player/dashboard" : "/player/login"), [loggedIn, navigate]);

  const seasonOptions = allSeasons.map((s) => ({ id: s.id, seasonName: s.name || s.id }));

  return (
    <div
      className="lt-root"
      data-brand={tab}
      data-mode={dark ? "dark" : "light"}
    >
      <AccentBar />
      {!navigator.onLine && (
        <div className="lt-offline">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 18, height: 18 }}>
            <line x1="1" y1="1" x2="23" y2="23" />
            <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
            <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
            <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
            <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
            <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
            <line x1="12" y1="20" x2="12.01" y2="20" />
          </svg>
          You're offline — some data may be outdated.
        </div>
      )}
      <Nav
        brand={tab}
        dark={dark}
        onToggleMode={() => setDarkOverride(!dark)}
        loggedIn={loggedIn}
        isAdmin={!!(loggedIn && isAdmin)}
        onLogin={handleLogin}
        onDash={handleDash}
        onOpenDrawer={() => setDrawerOpen(true)}
      />
      <Marquee items={announcements} />
      {tab === "spl" && <RegBanner onRegister={handleRegister} />}
      <Hero brand={tab} tab={tab} setTab={changeTab} heroStats={heroStats} />

      <main>
        {tab === "spl" && (
          <SPLPage
            rankings={rankings}
            divisions={divisions}
            divComparison={divComparison}
            badges={badges}
            nextLeagueDay={nextLeagueDay}
            isLiveNow={isLiveNow}
            loggedIn={loggedIn}
            currentUserId={currentUserId}
            onH2H={(t) => setH2hTarget(t)}
            onRegister={handleRegister}
            selectedSeason={seasonId || ""}
            allSeasons={seasonOptions}
            onSeasonChange={(id) => setSelectedSeason(id)}
          />
        )}
        {tab === "spc" && (
          <section className="lt-section">
            <div className="lt-wrap">
              <div className="lt-section-head">
                <div>
                  <div className="lt-kicker">Cup competitions</div>
                  <h2>Active &amp; past cups</h2>
                  <p>Fixed showcase competitions — national teams and knockout glory.</p>
                </div>
              </div>
              <SPCContent />
            </div>
          </section>
        )}
        {tab === "spt" && (
          <section className="lt-section">
            <div className="lt-wrap">
              <div className="lt-section-head">
                <div>
                  <div className="lt-kicker">Open tournaments</div>
                  <h2>Upcoming &amp; live tournaments</h2>
                  <p>Register your team — spots fill fast.</p>
                </div>
              </div>
              <SPTContent
                tournaments={tournaments}
                navigate={navigate}
                loggedIn={!!playerUser}
                currentUserId={playerUser?.playerId}
                registrations={registrations}
                pendingInvitations={pendingInvitations}
                receivedInvitations={receivedInvitations}
                processingInviteId={processingInviteId}
                onAcceptInvitation={handleAcceptInvitation}
                onDeclineInvitation={handleDeclineInvitation}
                onCancelInvitation={handleCancelInvitation}
                onWithdrawRegistration={handleWithdrawRegistration}
              />
            </div>
          </section>
        )}
      </main>

      <Footer brand={tab} />

      {h2hTarget && (
        <H2HDialog initial={h2hTarget} allPlayers={rankings} onClose={() => setH2hTarget(null)} />
      )}
      {drawerOpen && (
        <AdminDrawer onClose={() => setDrawerOpen(false)} navigate={navigate} />
      )}
    </div>
  );
}
